import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ReceivedEInvoicesTable } from "@/components/ReceivedEInvoicesTable";
import { NetworkReferenceSearch } from "@/components/NetworkReferenceSearch";
import type { components } from "@dolfin/api-admin";

type ReceivedEInvoiceState = NonNullable<components["schemas"]["ReceivedEInvoiceState"]>;


/** "Needs action" by default: an unplaced document exists nowhere else, and the sender believes it was delivered. */
const routedOptions: { value: string; label: string }[] = [
  { value: "false", label: "Needs action (unrouted)" },
  { value: "any", label: "All deliveries" },
  { value: "true", label: "Placed successfully" },
];

const stateOptions: { value: ReceivedEInvoiceState | "any"; label: string }[] = [
  { value: "any", label: "All states" },
  { value: "Unroutable", label: "Unroutable - no organisation claims it" },
  { value: "Unreadable", label: "Unreadable - no usable document" },
  { value: "Received", label: "Received - not yet resolved" },
  { value: "Routed", label: "Routed - placed with an organisation" },
];

export function ReceivedEInvoicesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);

  const routedParam = searchParams.get("routed");
  const routed = routedOptions.some((o) => o.value === routedParam)
    ? (routedParam as string)
    : "false";

  const stateParam = searchParams.get("state");
  const state: ReceivedEInvoiceState | "any" = stateOptions.some((o) => o.value === stateParam)
    ? (stateParam as ReceivedEInvoiceState | "any")
    : "any";

  const reference = searchParams.get("ref") ?? undefined;

  function updateParam(key: string, value: string | null) {
    setSearchParams(
      (params) => {
        if (value) {
          params.set(key, value);
        } else {
          params.delete(key);
        }
        return params;
      },
      { replace: true },
    );
  }

  // Refresh on an interval as well as on demand: the routing sweep runs every 15 minutes and can
  // empty this queue without anyone touching the page.
  useEffect(() => {
    const timer = setInterval(() => setRefreshKey((k) => k + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="mx-auto w-full max-w-[100rem] p-8">
      <h1 className="mb-1 text-2xl font-bold">Received E-Invoices</h1>
      <p className="mb-6 max-w-3xl text-sm text-muted-foreground">
        Every document delivered to us over an e-invoicing network, across organisations. A delivery
        that could not be placed belongs to nobody, so it appears nowhere else - not to the customer,
        and not as a bill. Newest first, paged.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <NetworkReferenceSearch value={reference} onChange={(v) => updateParam("ref", v)} />
        {/* A network-id search spans the whole list, so the placement filter is shown as set aside. */}
        <Select
          value={reference ? "any" : routed}
          disabled={!!reference}
          onValueChange={(v) => updateParam("routed", v === "false" ? null : v)}
        >
          <SelectTrigger className="w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {routedOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={state}
          onValueChange={(v) => updateParam("state", v === "any" ? null : v)}
        >
          <SelectTrigger className="w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {stateOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => setRefreshKey((k) => k + 1)}>
          <RefreshCw className="mr-2 size-4" />
          Refresh
        </Button>
      </div>

      <div className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600" />
        <p className="text-muted-foreground">
          An unroutable document usually means drift rather than a stranger: the party is registered
          with the provider but no organisation here claims that participant id — often registered in
          the provider&apos;s console instead of through our API. Registering the organisation, or
          correcting its tax number, is what resolves it. The routing sweep then places it within
          fifteen minutes; nothing here needs to be re-sent.
        </p>
      </div>

      <ReceivedEInvoicesTable
        // A network-id search looks across the whole list: the default "needs action" filter would
        // otherwise hide the (placed) document someone is asking about.
        routedSuccess={reference || routed === "any" ? undefined : routed === "true"}
        state={state === "any" ? undefined : state}
        networkReference={reference}
        refreshKey={refreshKey}
      />
    </div>
  );
}
